<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBSemi from '@/types/ferme/semi'
import type MDBAlerte from '@/types/alerte';
import { notifyError, toDate_wd_dd_MMMM, toDate_wd_dd_MMMM_hh_mm } from '@/utils/helpers'
import type MDBVegcoBucket from '@/types/vegco/bucket';

definePage({
  meta: {
    id: 'alertes',
    title: 'Alertes',
    icon: 'mdi-bell-outline',
    drawerIndex: 1,
    hideInDrawer: true,
    requiresAuth: true
  },
})

const theme = useTheme()
const storeU = useUtilisateurStore()
const appStore = useAppStore()
const router = useRouter()

const alertes = ref([] as MDBAlerte[])
const search = ref('')
const isLoading = ref(true)
const voirConfirmés = ref(false)

interface SévéritéInfo {
  label: string
  color: string
  icon: string
}

function getSévérité(sévérité: number): SévéritéInfo {
  switch (sévérité) {
    case 1: return { label: 'Info', color: 'info', icon: 'mdi-information-outline' }
    case 2: return { label: 'Avertissement', color: 'warning', icon: 'mdi-alert-outline' }
    case 3: return { label: 'Erreur', color: 'error', icon: 'mdi-alert-circle-outline' }
    case 4: return { label: 'Critique', color: 'error', icon: 'mdi-alert-octagon-outline' }
    default: return { label: 'Debug', color: 'grey', icon: 'mdi-bug-outline' }
  }
}

const MESSAGE_MAX_LENGTH = 180
const expandedMessages = ref(new Set<string>())

function isMessageLong(message: string): boolean {
  return (message?.length ?? 0) > MESSAGE_MAX_LENGTH
}

function getMessagePreview(message: string): string {
  return message.slice(0, MESSAGE_MAX_LENGTH).trimEnd() + '…'
}

function toggleMessage(id: string) {
  if (expandedMessages.value.has(id))
    expandedMessages.value.delete(id)
  else
    expandedMessages.value.add(id)
  expandedMessages.value = new Set(expandedMessages.value)
}
const headers: DataTableHeaders = [
  { title: 'Date', key: 'formattedCréeLe', width: '140px', sortable: false },
  { title: 'Sévérité', key: 'sévérité', width: '80px', sortable: false },
  { title: 'Type', key: 'typeAlerte', width: '180px', sortable: false },
  { title: 'Sujet', key: 'sujet', width: '110px', sortable: false },
  { title: 'Message', key: 'message', sortable: false },
  { title: 'Confirmé le', key: 'formattedConfirméLe', width: '130px', sortable: false },
  { title: 'Confirmé par', key: 'confirméPar', width: '140px', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
];
const formattedAlertes = computed(() => {
  let al = alertes.value.filter(alerte => alerte.destinataireType !== 'Agrimétrie')

  if (!voirConfirmés.value) {
    al = al.filter(alerte => {
      return new Date(alerte.confirméLe).getFullYear() <= 2000
    })
  }

  return al
    .slice()
    .sort((a, b) => new Date(b.crééLe).getTime() - new Date(a.crééLe).getTime())
    .map(alerte => ({
      ...alerte,
      formattedCréeLe: toDate_wd_dd_MMMM_hh_mm(alerte.crééLe),
      sujet: alerte.typeSujet,
      message: alerte.message || 'Aucun message',
      formattedConfirméLe: new Date(alerte.confirméLe).getFullYear() > 2000 ? toDate_wd_dd_MMMM(alerte.confirméLe) : 'Non confirmé',
    }));
})

function getSujet(alerte: MDBAlerte) {
  if (alerte.typeSujet === 'Semi' || alerte.typeSujet === 'Semi.Buckets') {
    return 'Semi'
  } else if (alerte.typeSujet === 'Vegco.Commande') {
    return 'Commande'
  } else if (alerte.typeSujet === 'Vegco.Bucket') {
    return 'Bucket'
  }
  return alerte.typeSujet;
}

async function openSujet(alerte: MDBAlerte) {
  try {
    if (alerte.typeSujet === 'Semi' || alerte.typeSujet === 'Semi.Buckets') {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const results = await apiProphet.get(`sites/${siteCS}/semis/${alerte.sujetId}`)
      const semi = results.data as MDBSemi

      if (alerte.typeSujet === 'Semi') {
        editSemi(semi)
      } else if (alerte.typeSujet === 'Semi.Buckets') {
        openBuckets(semi);
      }
    } else if (alerte.typeSujet === 'Vegco.Commande') {
      router.push('/vegco/commandes')
    } else if (alerte.typeSujet === 'Vegco.Bucket') {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const results = await apiProphet.get(`vgco/sites/${siteCS}/buckets/${alerte.sujetId}`)
      const bucket = results.data as MDBVegcoBucket
      openBucket(bucket);
    }
  }
  catch (error) {
    notifyError(error);
  }
}

async function confirmer(alerte: MDBAlerte, confirmer: boolean) {
  try {
    const config = { params: { confirmer: confirmer } }
    const result = await apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config)
    if (result.status == 200) {
      Notify.success('Alerte confirmée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error);
  }
}

const alertesNonConfirmées = computed(() =>
  formattedAlertes.value.filter(alerte => alerte.confirméPar == '')
)

const dialogToutConfirmer = ref(false)
const isConfirmingTout = ref(false)

async function confirmerTout() {
  try {
    isConfirmingTout.value = true
    const config = { params: { confirmer: true } }
    const àConfirmer = alertesNonConfirmées.value
    await Promise.all(
      àConfirmer.map(alerte =>
        apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config)
      )
    )
    Notify.success(`${àConfirmer.length} alerte(s) confirmée(s) avec succès`)
    dialogToutConfirmer.value = false
    await getAlertes()
  } catch (error) {
    notifyError(error);
  } finally {
    isConfirmingTout.value = false
  }
}

async function effacer(alerte: MDBAlerte) {
  try {
    const result = await apiProphet.delete(`api/alertes/${alerte.id}`)
    if (result.status == 200) {
      Notify.success('Alerte effacée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error);
  }
}

const dialogSemi = useTemplateRef('dialogSemi')
async function editSemi(semi: MDBSemi) {
  await dialogSemi.value?.openSemiEdit(semi)
}

const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets(semi: MDBSemi) {
  await dialogBuckets.value?.openBucketsAsync(semi, null)
}

const dialogBucket = useTemplateRef('dialogBucket')
async function openBucket(bucket: MDBVegcoBucket) {
  await dialogBucket.value?.openBucketEdit(bucket, undefined)
}

watch(() => storeU.siteSélectionné, async () => { await getAlertes() })

async function getAlertes() {
  try {
    isLoading.value = true;
    alertes.value = [];

    const results1 = await apiProphet.get('api/utilisateur/alertes')
    alertes.value = alertes.value.concat(results1.data as MDBAlerte[]);

    if (storeU.siteSélectionné.id) {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const results = await apiProphet.get(`sites/${siteCS}/alertes`)
      alertes.value = alertes.value.concat(results.data as MDBAlerte[]);
    }

    if (storeU.utilisateur.estAdministrateur) {
      const results2 = await apiProphet.get('api/alertes/systeme')
      alertes.value = alertes.value.concat(results2.data as MDBAlerte[]);
    }

    appStore.alertCount = alertes.value.filter(a =>
      a.destinataireType !== 'Agrimétrie' &&
      new Date(a.confirméLe).getFullYear() <= 2000
    ).length
  }
  catch (error) {
    notifyError(error);
  }
  finally {
    isLoading.value = false;
  }
}

const dialogNuméro = ref(false)
const numéroTéléphone = ref(storeU.utilisateur.paramètresAlertes.numéroTéléphone || '')
const numéroTéléphoneE164 = computed(() => {
  // Remove all non-digit characters and prepend +1
  const digits = numéroTéléphone.value.replace(/\D/g, '');
  return digits.length ? `+1${digits}` : '';
})
const heureDe = ref(storeU.utilisateur.paramètresAlertes.heureDe || 0)
const heureÀ = ref(storeU.utilisateur.paramètresAlertes.heureÀ || 24)

function openTéléphone() {
  dialogNuméro.value = true
  numéroTéléphone.value = storeU.utilisateur.paramètresAlertes.numéroTéléphone || ''
}

async function confirmTéléphone() {
  try
  {
    storeU.utilisateur.paramètresAlertes.numéroTéléphone = numéroTéléphone.value
    storeU.utilisateur.paramètresAlertes.numéroTéléphoneE164 = numéroTéléphoneE164.value
    storeU.utilisateur.paramètresAlertes.heureDe = heureDe.value
    storeU.utilisateur.paramètresAlertes.heureÀ = heureÀ.value
    await storeU.updateParamètresAlertes()
    dialogNuméro.value = false
  }
  catch (error) {
    notifyError(error);
  }
}

function cancelTéléphone() {
  dialogNuméro.value = false
}

onMounted(() => {
  getAlertes();
});
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-alert-octagon-outline" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Alertes</div>
            <div class="text-caption text-medium-emphasis">Notifications et messages reçus</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn
            v-if="alertesNonConfirmées.length > 0"
            color="success" variant="tonal" rounded="pill"
            prepend-icon="mdi-check-all"
            @click="dialogToutConfirmer = true">
            Tout confirmer
          </v-btn>
          <v-btn
            color="primary" variant="tonal" rounded="pill"
            prepend-icon="mdi-cellphone-message"
            @click="openTéléphone()">
            SMS
          </v-btn>
        </div>
      </div>

      <!-- Secondary row: search + filter -->
      <div class="d-flex align-center flex-wrap mt-3" style="gap: 12px;">
        <v-text-field
          v-model="search"
          label="Rechercher"
          prepend-inner-icon="mdi-magnify"
          density="compact" variant="outlined" rounded="lg" hide-details clearable
          style="max-width: 320px; min-width: 180px;"/>
        <v-spacer/>
        <v-switch
          v-model="voirConfirmés"
          label="Voir confirmés"
          color="primary"
          class="flex-grow-0" hide-details density="compact"/>
      </div>
    </div>
    <v-divider/>

    <v-data-table
      :headers="headers"
      :items="formattedAlertes"
      :search="search"
      :loading="isLoading"
      item-value="id"
      :hide-default-header="$vuetify.display.smAndDown"
      no-data-text="Aucune alerte trouvée">

      <!-- Severity column -->
      <template #item.sévérité="{ item }">
        <v-chip
          :color="getSévérité(item.sévérité).color"
          :prepend-icon="getSévérité(item.sévérité).icon"
          size="small" variant="tonal" label>
          {{ getSévérité(item.sévérité).label }}
        </v-chip>
      </template>

      <!-- Message column with truncation -->
      <template #item.message="{ item }">
        <div class="message-cell py-2">
          <div class="message-text" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
          <v-btn
            v-if="isMessageLong(item.message)"
            size="x-small" color="primary" variant="text" class="mt-1 px-1"
            :append-icon="expandedMessages.has(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            @click="toggleMessage(item.id)">
            {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
          </v-btn>
        </div>
      </template>

      <!-- Confirmé status column -->
      <template #item.formattedConfirméLe="{ item }">
        <v-chip
          v-if="item.confirméPar == ''"
          color="grey" size="small" variant="tonal" label
          prepend-icon="mdi-clock-outline">
          Non confirmé
        </v-chip>
        <v-chip
          v-else
          color="success" size="small" variant="tonal" label
          prepend-icon="mdi-check-circle-outline">
          {{ item.formattedConfirméLe }}
        </v-chip>
      </template>

      <template #item.sujet="{ item }">
        <v-btn
          v-if="item.typeSujet !== ''"
          size="small" color="primary" variant="tonal" rounded="lg"
          @click="openSujet(item)">
          {{ getSujet(item) }}
        </v-btn>
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex align-center" style="gap: 4px;">
          <v-tooltip :text="item.confirméPar == '' ? 'Confirmer' : 'Enlever la confirmation'" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                :icon="item.confirméPar == '' ? 'mdi-check' : 'mdi-undo-variant'"
                :color="item.confirméPar == '' ? 'success' : 'warning'"
                size="small" variant="text"
                @click="confirmer(item, item.confirméPar == '')"/>
            </template>
          </v-tooltip>
          <v-tooltip v-if="storeU.utilisateur.estAdministrateur" text="Effacer" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-delete-outline" color="error"
                size="small" variant="text"
                @click="effacer(item)"/>
            </template>
          </v-tooltip>
        </div>
      </template>

      <!-- Mobile card layout -->
      <template v-if="$vuetify.display.smAndDown" #item="{ item }">
        <tr>
          <td class="pa-0">
            <v-card variant="flat" class="alerte-mobile-card pa-3 ma-2" rounded="lg">
              <div class="d-flex align-center justify-space-between mb-2" style="gap: 8px;">
                <v-chip
                  :color="getSévérité(item.sévérité).color"
                  :prepend-icon="getSévérité(item.sévérité).icon"
                  size="small" variant="tonal" label>
                  {{ getSévérité(item.sévérité).label }}
                </v-chip>
                <v-chip
                  :color="item.confirméPar == '' ? 'grey' : 'success'"
                  :prepend-icon="item.confirméPar == '' ? 'mdi-clock-outline' : 'mdi-check-circle-outline'"
                  size="x-small" variant="tonal" label>
                  {{ item.confirméPar == '' ? 'Non confirmé' : 'Confirmé' }}
                </v-chip>
              </div>

              <div class="text-body-2 font-weight-bold">{{ item.typeAlerte }}</div>
              <div class="text-caption text-medium-emphasis mb-2">{{ item.formattedCréeLe }}</div>

              <div class="text-body-2 mb-1" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
              <v-btn
                v-if="isMessageLong(item.message)"
                size="x-small" color="primary" variant="text" class="mb-2 px-1"
                :append-icon="expandedMessages.has(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                @click="toggleMessage(item.id)">
                {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
              </v-btn>

              <div class="d-flex flex-column text-caption text-medium-emphasis mb-2" style="gap: 2px;">
                <span><v-icon icon="mdi-account-outline" size="14" class="me-1"/>Créé par : {{ item.crééPar }}</span>
              </div>

              <v-divider class="my-2"/>

              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-btn
                  v-if="item.typeSujet !== ''"
                  size="small" color="primary" variant="tonal" rounded="lg"
                  prepend-icon="mdi-open-in-new"
                  @click="openSujet(item)">
                  {{ getSujet(item) }}
                </v-btn>
                <v-spacer/>
                <v-btn
                  :prepend-icon="item.confirméPar == '' ? 'mdi-check' : 'mdi-undo-variant'"
                  :color="item.confirméPar == '' ? 'success' : 'warning'"
                  size="small" variant="text"
                  @click="confirmer(item, item.confirméPar == '')">
                  {{ item.confirméPar == '' ? 'Confirmer' : 'Annuler' }}
                </v-btn>
                <v-btn
                  v-if="storeU.utilisateur.estAdministrateur"
                  icon="mdi-delete-outline" color="error"
                  size="small" variant="text"
                  @click="effacer(item)"/>
              </div>
            </v-card>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>

  <DialogSemi ref='dialogSemi'/>
  <DialogBuckets ref="dialogBuckets"/>
  <DialogBucket ref="dialogBucket"/>

  <v-dialog v-model="dialogNuméro" max-width="600px" :fullscreen="$vuetify.display.xs">
    <v-card rounded="xl">
      <div class="dialog-header pa-4 d-flex align-center" style="gap: 12px;">
        <v-avatar color="white" variant="tonal" size="40" rounded="lg">
          <v-icon icon="mdi-cellphone-message" size="22"/>
        </v-avatar>
        <div>
          <div class="text-h6 font-weight-bold">Alertes par SMS</div>
          <div class="text-caption" style="opacity: 0.85;">Configurez votre numéro et vos heures de réception</div>
        </div>
      </div>
      <v-card-text class="pt-4">
        <v-container class="pa-0">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="numéroTéléphone"
                type="tel"
                variant="outlined" rounded="lg"
                prepend-inner-icon="mdi-phone-outline"
                placeholder="123-456-7890"
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                :hint="`Format E.164 : ${numéroTéléphoneE164 || '—'}`" persistent-hint
                label="Numéro de téléphone"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="heureDe"
                type="number"
                variant="outlined" rounded="lg"
                prepend-inner-icon="mdi-clock-start"
                min="0" max="24"
                label="Heure minimale"
                placeholder="0-23"/>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="heureÀ"
                type="number"
                variant="outlined" rounded="lg"
                prepend-inner-icon="mdi-clock-end"
                min="0" max="24"
                label="Heure maximale"
                placeholder="0-23"/>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider/>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="cancelTéléphone">Annuler</v-btn>
        <v-btn color="primary" variant="flat" rounded="pill" @click="confirmTéléphone">Confirmer</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="dialogToutConfirmer" max-width="460px" :fullscreen="$vuetify.display.xs">
    <v-card rounded="xl">
      <div class="dialog-header pa-4 d-flex align-center" style="gap: 12px;">
        <v-avatar color="white" variant="tonal" size="40" rounded="lg">
          <v-icon icon="mdi-check-all" size="22"/>
        </v-avatar>
        <div>
          <div class="text-h6 font-weight-bold">Tout confirmer</div>
          <div class="text-caption" style="opacity: 0.85;">Confirmer toutes les alertes affichées</div>
        </div>
      </div>
      <v-card-text class="pt-4">
        Voulez-vous vraiment confirmer
        <strong>{{ alertesNonConfirmées.length }}</strong>
        alerte(s) non confirmée(s) ? Cette action ne peut pas être annulée en masse.
      </v-card-text>
      <v-divider/>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" :disabled="isConfirmingTout" @click="dialogToutConfirmer = false">Annuler</v-btn>
        <v-btn
          color="success" variant="flat" rounded="pill"
          :loading="isConfirmingTout"
          @click="confirmerTout">
          Tout confirmer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Message cell ────────────────────────── */
.message-cell {
  max-width: 480px;
}
.message-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* ── Mobile alert card ───────────────────── */
.alerte-mobile-card {
  border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"');
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.18)" : "rgba(var(--v-theme-primary), 0.02)"');
}

/* ── Dialog header ───────────────────────── */
.dialog-header {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}
</style>
